'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { savedCardsService, type SavedCardView } from '../services/savedCards';
import { createLogger } from '../utils/logger';

const logger = createLogger('useSavedCards');

/**
 * Query key factory for a customer's saved cards. Namespaced by
 * (tenantDocId, restaurantDocId) — saved cards are merchant-scoped, so a
 * restaurant switch automatically reads a different card set.
 */
export const savedCardKeys = {
  all: ['saved-cards'] as const,
  scope: (tenantDocId: string, locationDocId: string) =>
    [...savedCardKeys.all, tenantDocId, locationDocId] as const,
};

/**
 * Fetch the signed-in customer's saved cards at one restaurant. Disabled until
 * both ids are known. The endpoint requires a verified-phone session; a
 * non-phone customer gets a 403 the caller can surface as "verify your phone".
 */
export const useSavedCards = (tenantDocId?: string, locationDocId?: string) => {
  const enabled = !!tenantDocId && !!locationDocId;
  return useQuery<SavedCardView[]>({
    queryKey: enabled
      ? savedCardKeys.scope(tenantDocId!, locationDocId!)
      : savedCardKeys.all,
    queryFn: () => savedCardsService.listSavedCards(tenantDocId!, locationDocId!),
    enabled,
    staleTime: 30_000,
  });
};

/**
 * Remove a saved card (drops the customer index doc + purges the provider
 * vault server-side). Invalidates the scoped list on success.
 */
export const useDeleteSavedCard = (tenantDocId: string, locationDocId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (methodId: string) =>
      savedCardsService.deleteSavedCard(tenantDocId, locationDocId, methodId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: savedCardKeys.scope(tenantDocId, locationDocId),
      });
    },
    onError: (error) => {
      logger.error('Failed to delete saved card', error);
    },
  });
};
